import { z } from 'zod';

import { timelineItemSchema } from './console.js';

/**
 * Conversaciones: TODAS las respuestas de los leads, una fila por conversación. La fuente es
 * `email_messages` (backfill de Instantly `GET /emails` + webhook + DBs de los stacks + envíos de
 * Gmail ingeridos). Las escalaciones del bot llegan por el sync de las DBs viejas
 * (`leads.escalations`) como `meta.escalation` del mensaje entrante del lead.
 *
 * Conversación = hilo del Unibox (`instantly_thread_id`); si el mensaje no lo trae (webhook recién
 * llegado, DB vieja), el hilo más reciente del mismo lead en la misma campaña; si no hay ninguno,
 * lead + campaña.
 */

const isoDate = z.string();
const boolParam = z.enum(['true', 'false']).transform((v) => v === 'true');
const dayParam = z.iso.date();

/**
 * Estado derivado de la conversación, mirando el ÚLTIMO mensaje del lead que no es automático:
 *  - `attended`  alguien la atendió: respuesta manual después (Unibox a mano o Gmail), escalación
 *                marcada como atendida o «Marcar como atendida» en el panel.
 *  - `escalated` el asistente la derivó a una persona y nadie la atendió todavía.
 *  - `bot`       la contestó el asistente (con o sin dossier).
 *  - `pending`   nadie le respondió.
 *  - `auto`      todo lo que llegó son respuestas automáticas (fuera de oficina, tickets…).
 */
export const CONVERSATION_STATUSES = ['pending', 'escalated', 'bot', 'attended', 'auto'] as const;
export const conversationStatusSchema = z.enum(CONVERSATION_STATUSES);
export type ConversationStatus = z.infer<typeof conversationStatusSchema>;

export const CONVERSATION_STATUS_LABELS: Record<ConversationStatus, string> = {
  pending: 'Por responder',
  escalated: 'Derivada',
  bot: 'Respondida por el asistente',
  attended: 'Atendida',
  auto: 'Respuesta automática',
};

export const conversationsQuerySchema = z.object({
  /**
   * Sin estado = todas (las automáticas sólo con `includeAuto=true`). `open` = las que esperan a una
   * persona (por responder + derivadas): lo que cuenta el menú y el resumen.
   */
  status: z.enum([...CONVERSATION_STATUSES, 'open']).optional(),
  includeAuto: boolParam.optional(),
  tenant: z.uuid().optional(),
  campaign: z.uuid().optional(),
  /** Último mensaje del lead desde / hasta (días, inclusive, hora de Madrid). */
  from: dayParam.optional(),
  to: dayParam.optional(),
  q: z.string().trim().max(200).optional(),
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type ConversationsQuery = z.input<typeof conversationsQuerySchema>;
export type ConversationsFilter = z.infer<typeof conversationsQuerySchema>;

export const kbMatchSchema = z.object({
  id: z.union([z.string(), z.number()]).nullable(),
  similarity: z.number().nullable(),
  preview: z.string().nullable(),
});
export type KbMatch = z.infer<typeof kbMatchSchema>;

export const escalationDtoSchema = z.object({
  legacyId: z.string().nullable(),
  status: z.string().nullable(),
  reason: z.string().nullable(),
  source: z.string().nullable(),
  notifyStatus: z.string().nullable(),
  draft: z.string().nullable(),
  bestSimilarity: z.number().nullable(),
  kbMatches: z.array(kbMatchSchema),
  handledAt: isoDate.nullable(),
  handledBy: z.string().nullable(),
});
export type EscalationDto = z.infer<typeof escalationDtoSchema>;

export const conversationRowSchema = z.object({
  /** Id del último mensaje del lead (sirve para abrir la conversación). */
  id: z.uuid(),
  tenantId: z.uuid(),
  tenantSlug: z.string(),
  tenantName: z.string(),
  leadEmail: z.string(),
  leadId: z.uuid().nullable(),
  company: z.string().nullable(),
  campaignId: z.uuid().nullable(),
  campaignName: z.string().nullable(),
  subject: z.string().nullable(),
  /** Texto del último mensaje del lead, sin la cita del correo anterior. */
  preview: z.string().nullable(),
  lastMessageAt: isoDate,
  /** Mensajes del lead en la conversación (incluye los automáticos). */
  inboundCount: z.number(),
  status: conversationStatusSchema,
  hasEscalation: z.boolean(),
});
export type ConversationRow = z.infer<typeof conversationRowSchema>;

export const conversationCountsSchema = z.object({
  all: z.number(),
  open: z.number(),
  pending: z.number(),
  escalated: z.number(),
  bot: z.number(),
  attended: z.number(),
  auto: z.number(),
});
export type ConversationCounts = z.infer<typeof conversationCountsSchema>;

export const conversationsResponseSchema = z.object({
  rows: z.array(conversationRowSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
  /** Por estado, con el resto de los filtros aplicados. `all` no cuenta las automáticas; `open` = pending + escalated. */
  counts: conversationCountsSchema,
  /** Campañas con conversaciones en el alcance (para el filtro). */
  campaigns: z.array(
    z.object({ id: z.uuid(), name: z.string(), tenantSlug: z.string(), count: z.number() }),
  ),
});
export type ConversationsResponse = z.infer<typeof conversationsResponseSchema>;

export const conversationDetailSchema = z.object({
  conversation: conversationRowSchema,
  /** Toda la conversación con el lead en esa empresa: pasos de la campaña, respuestas, asistente. */
  thread: z.array(timelineItemSchema),
  /** Escalación del último mensaje derivado de la conversación (borrador, KB, motivo). */
  escalation: escalationDtoSchema.nullable(),
  attended: z
    .object({ at: isoDate, by: z.string().nullable(), notes: z.string().nullable() })
    .nullable(),
  /** Link al hilo en el Unibox de Instantly (del webhook) o al Unibox en general. */
  uniboxUrl: z.string().nullable(),
  /** Puede marcarla como atendida (permiso `inbox:write` en esa empresa y no está atendida). */
  writable: z.boolean(),
  legacyWritesEnabled: z.boolean(),
});
export type ConversationDetail = z.infer<typeof conversationDetailSchema>;

export const attendConversationSchema = z.object({
  notes: z.string().trim().max(2000).optional(),
});
export type AttendConversation = z.infer<typeof attendConversationSchema>;

export const attendConversationResponseSchema = z.object({
  ok: z.literal(true),
  /** Escalaciones del stack que se marcaron atendidas en el mismo paso. */
  escalationsHandled: z.number(),
});
